import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  GasThreadPhysicoChemicalIndicator,
  GasThreadPhysicoChemicalIndicatorCreateResponse,
  GasThreadPhysicoChemicalIndicatorFilters,
  GasThreadPhysicoChemicalIndicatorForm,
  GasThreadPhysicoChemicalIndicatorViewModel,
} from './types';

export const gasThreadPhysicoChemicalIndicatorApi = createApi({
  reducerPath: 'gasThreadPhysicoChemicalIndicatorApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gas-thread-physico-chemical-indicators`,
  }),
  tagTypes: [
    'GasThreadPhysicoChemicalIndicator',
    'GasThreadPhysicoChemicalIndicator',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getGasThreadPhysicoChemicalIndicatorI: builder.infiniteQuery<
      GasThreadPhysicoChemicalIndicatorViewModel,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['GasThreadPhysicoChemicalIndicator'],
    }),
    getGasThreadPhysicoChemicalIndicator: builder.query<
      GasThreadPhysicoChemicalIndicatorViewModel[],
      GasThreadPhysicoChemicalIndicatorFilters
    >({
      query: params => ({
        url: `/query`,
        params,
      }),
      providesTags: () => ['GasThreadPhysicoChemicalIndicator'],
    }),
    getGasThreadPhysicoChemicalIndicatorById: builder.query<
      GasThreadPhysicoChemicalIndicator,
      string
    >({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: [
        'GasThreadPhysicoChemicalIndicator',
        'GasThreadPhysicoChemicalIndicator',
      ],
    }),
    createGasThreadPhysicoChemicalIndicator: builder.mutation<
      GasThreadPhysicoChemicalIndicatorCreateResponse,
      GasThreadPhysicoChemicalIndicatorForm
    >({
      query: data => ({
        url: ``,
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'GasThreadPhysicoChemicalIndicator',
        {
          type: 'GasThreadPhysicoChemicalIndicator',
          id: result?.id,
        },
      ],
    }),
    updateGasThreadPhysicoChemicalIndicatorById: builder.mutation<
      string,
      GasThreadPhysicoChemicalIndicatorForm
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'GasThreadPhysicoChemicalIndicator',
        { type: 'GasThreadPhysicoChemicalIndicator', id: result },
      ],
    }),
  }),
});

export const {
  useGetGasThreadPhysicoChemicalIndicatorQuery,
  useGetGasThreadPhysicoChemicalIndicatorByIdQuery,
  useCreateGasThreadPhysicoChemicalIndicatorMutation,
  useUpdateGasThreadPhysicoChemicalIndicatorByIdMutation,
} = gasThreadPhysicoChemicalIndicatorApi;

export const useGetGasThreadPhysicoChemicalIndicatorInfiniteQuery =
  normalizeInfiniteQueryData(
    gasThreadPhysicoChemicalIndicatorApi.useGetGasThreadPhysicoChemicalIndicatorIInfiniteQuery,
  );
